import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Calendar, Clock, HelpCircle, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Nava Input — matches the Figma Input spec.
 * A bordered box wrapping a native <input>. Two affix tiers:
 *  - `leading`/`trailing`: in-padding content (icon, text, clear/eye) inside the box.
 *  - `leadingSegment`/`trailingSegment`: flush cells with their own border seam,
 *    outside the padding (e.g. a Copy button, ± steppers, or a Select later).
 * Sizing is emergent from padding (text control), colored by `status`.
 */
const boxVariants = cva(
  // Focus halo matches Figma exactly: box-shadow of a 2px white gap then a 4px
  // soft colored ring (ring-offset + ring). The BORDER COLOR DOES NOT CHANGE on
  // focus — only the shadow appears. Ring color is the light `focus-*` tint.
  // The ring is a 2px white gap (ring-offset) + 4px light tint (ring). For the
  // default status the ring only appears on focus; for error/success the ring is
  // ALWAYS visible (it communicates status at rest), per Figma. Ring tints are the
  // light -3/-6/-5 steps the component uses — the semantic focus-* tokens are the
  // saturated -9 step and don't match, so we use the correct tints directly.
  'flex w-full rounded-[0.375rem] border bg-canvas outline-none transition-shadow ring-offset-2 ring-offset-background has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-60 has-[input:disabled]:bg-disabled',
  {
    variants: {
      status: {
        default: 'border-border focus-within:ring-2 focus-within:ring-[#a9f1ca]',
        error: 'border-border-danger ring-2 ring-[#f9c6c6]',
        success: 'border-border-success ring-2 ring-[#ceebcf]',
      },
    },
    defaultVariants: { status: 'default' },
  },
);

/** Padding + type per size — applied to the content cell so height is emergent. */
const contentVariants = cva('flex min-w-0 flex-1 items-center gap-space-8', {
  variants: {
    size: {
      xs: 'px-space-8 py-space-4 type-text-xs [&_svg]:size-icon-sm',
      sm: 'px-space-8 py-space-6 type-text-sm [&_svg]:size-icon-md',
      md: 'px-space-8 py-space-6 type-text-sm [&_svg]:size-icon-md',
      lg: 'px-space-12 py-space-8 type-text-sm [&_svg]:size-icon-lg',
      xl: 'px-space-12 py-space-8 type-text-md [&_svg]:size-icon-lg',
    },
  },
  defaultVariants: { size: 'md' },
});

type InputStatus = NonNullable<VariantProps<typeof boxVariants>['status']>;
type InputSize = NonNullable<VariantProps<typeof contentVariants>['size']>;

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'prefix'
> {
  ref?: React.Ref<HTMLInputElement>;
  /** Native input type. */
  type?: 'text' | 'password' | 'email' | 'url' | 'number' | 'search' | 'date' | 'time';
  size?: InputSize;
  status?: InputStatus;
  /** In-padding leading content — icon or text. */
  leading?: React.ReactNode;
  /** In-padding trailing content — icon or text (e.g. a password eye toggle). */
  trailing?: React.ReactNode;
  /** Flush leading cell with its own border seam — Button, stepper, or Select. */
  leadingSegment?: React.ReactNode;
  /** Flush trailing cell with its own border seam — Button, stepper, or Select. */
  trailingSegment?: React.ReactNode;
  /** Show a clear (✕) button when the field has a value. */
  clearable?: boolean;
  onClear?: () => void;
  label?: React.ReactNode;
  required?: boolean;
  /** Show a help icon next to the label. */
  helpIcon?: boolean;
  helperText?: React.ReactNode;
  /** Show a live "current/max" character counter (needs maxLength). */
  showCount?: boolean;
  /** Text alignment inside the field. Defaults to left; use center for steppers. */
  align?: 'left' | 'center' | 'right';
  className?: string;
}

/** Text input — 5 sizes, default/error/success, in-padding + segment affixes, clear, label, helper, count. */
export function Input({
  ref,
  size = 'md',
  status = 'default',
  type = 'text',
  leading,
  trailing,
  leadingSegment,
  trailingSegment,
  clearable,
  onClear,
  label,
  required,
  helpIcon,
  helperText,
  showCount,
  align = 'left',
  maxLength,
  value,
  defaultValue,
  onChange,
  disabled,
  id,
  className,
  ...rest
}: InputProps) {
  function alignClassFor(a: 'left' | 'center' | 'right') {
    if (a === 'center') return 'text-center';
    if (a === 'right') return 'text-right';
    return 'text-left';
  }
  const alignClass = alignClassFor(align);
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const helperId = `${inputId}-helper`;

  const [internal, setInternal] = React.useState(String(defaultValue ?? value ?? ''));
  const current = value !== undefined ? String(value) : internal;

  const leadingContent = leading ?? (type === 'search' ? <Search /> : null);
  // Native date/time glyphs ignore our tokens in dark mode — hide them and
  // paint a Lucide icon we control. The invisible indicator still receives clicks.
  let dateTimeIcon: React.ReactNode = null;
  if (type === 'date') {
    dateTimeIcon = <Calendar />;
  } else if (type === 'time') {
    dateTimeIcon = <Clock />;
  }
  const trailingContent = trailing ?? dateTimeIcon;
  const isDateTime = type === 'date' || type === 'time';
  const showClear = clearable && current.length > 0;
  const showHelper = Boolean(helperText || (showCount && maxLength != null));
  const hasSegments = Boolean(leadingSegment || trailingSegment);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setInternal(event.target.value);
    onChange?.(event);
  }

  function handleClear() {
    setInternal('');
    onClear?.();
    if (!onChange) return;
    const target = { value: '' } as HTMLInputElement;
    onChange({
      target,
      currentTarget: target,
    } as React.ChangeEvent<HTMLInputElement>);
  }

  const contentCell = (
    <div className={cn(contentVariants({ size }))}>
      {leadingContent && (
        <span className="flex shrink-0 items-center text-icon-secondary">{leadingContent}</span>
      )}
      <div className="relative min-w-0 flex-1">
        <input
          ref={ref}
          id={inputId}
          type={type}
          // Controlled if `value` is provided, else uncontrolled with defaultValue.
          {...(value !== undefined ? { value } : { defaultValue })}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          aria-invalid={status === 'error' || undefined}
          aria-describedby={showHelper ? helperId : undefined}
          onChange={handleChange}
          className={cn(
            'w-full min-w-0 bg-transparent text-foreground outline-none placeholder:text-quaternary disabled:cursor-not-allowed',
            isDateTime &&
              'pr-space-24 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0',
            alignClass,
          )}
          {...rest}
        />
        {isDateTime && trailingContent && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 flex items-center text-icon-secondary"
          >
            {trailingContent}
          </span>
        )}
      </div>
      {showClear && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear"
          className="flex shrink-0 cursor-pointer items-center text-icon-secondary hover:text-foreground"
        >
          <X />
        </button>
      )}
      {!isDateTime && trailingContent && (
        <span className="flex shrink-0 items-center text-icon-secondary">{trailingContent}</span>
      )}
    </div>
  );

  return (
    <div className={cn('flex w-full flex-col gap-space-6', className)}>
      {label && (
        <span className="flex items-center gap-space-4 type-text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-foreground-danger">*</span>}
          {helpIcon && <HelpCircle className="size-icon-md text-icon-tertiary" />}
        </span>
      )}

      <div className={cn(boxVariants({ status }), hasSegments && 'overflow-hidden')}>
        {leadingSegment && (
          <div className="flex shrink-0 items-stretch border-r border-border">{leadingSegment}</div>
        )}
        {contentCell}
        {trailingSegment && (
          <div className="flex shrink-0 items-stretch border-l border-border">
            {trailingSegment}
          </div>
        )}
      </div>

      {showHelper && (
        <div className="flex justify-between gap-space-8 type-text-xs text-tertiary">
          <span id={helperId} className={cn(status === 'error' && 'text-foreground-danger')}>
            {helperText}
          </span>
          {showCount && maxLength != null && (
            <span>
              {current.length}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
